// P157-R2: every visible piece of UI copy on the public /employer-register
// letter-first flow, in the languages a Thailand-registered employer actually
// reads. The employer half mirrors the worker page's publicInviteTranslations
// mechanism exactly (letter-language selector on the Agent side -> translated
// public page + a language switcher + a default inferred from the card), but a
// company reads this letter, not a migrant worker, so the language set is
// Thai + English rather than the migrant-language set. Backend-supplied values
// (Agent display name, appName, the Agent's own note text, the server's own
// message text) are NEVER translated here - only this app's own UI copy is,
// and callers interpolate the raw backend value into the translated sentence.
//
// EMPLOYER_LANGUAGES drives the topbar's language chip labels - each stays in
// its own native name, matching how a real language switcher reads.
export const EMPLOYER_LANGUAGES = [
  { code: "th", label: "ไทย" },
  { code: "en", label: "EN" },
];

const TRANSLATIONS = {
  th: {
    topbarTag: "ลงทะเบียนบริษัทนายจ้าง",

    loadingText: "กำลังเปิดจดหมายเชิญของคุณ...",

    envelopeEyebrow: "จดหมายเชิญลงทะเบียนนายจ้าง",
    envelopeFrom: (agentName) => `คุณได้รับจดหมายเชิญจาก ${agentName}`,
    envelopeHint: "กดเปิดจดหมายเพื่ออ่านรายละเอียด",
    envelopeOpenCta: "เปิดจดหมาย",
    envelopeFallbackAgent: "ผู้ดูแล",

    letterEyebrow: "จดหมายเชิญลงทะเบียนนายจ้าง",
    letterTitle: (agentName, appName) => `${agentName} เชิญบริษัทของคุณเข้าร่วม ${appName}`,
    letterDesc: "กรอกข้อมูลบริษัทของคุณเพื่อเริ่มต้นใช้งาน เมื่อส่งแล้วผู้ดูแลจะเห็นข้อมูลของคุณทันที",
    explainLines: [
      "ขั้นตอนนี้คือการลงทะเบียนข้อมูลบริษัทของคุณ",
      "หลังจากนี้ผู้ดูแลจะติดต่อกลับเพื่อยืนยันข้อมูล",
      "ยังไม่ใช่การทำสัญญาจ้างงาน และยังไม่มีการจับคู่แรงงานอัตโนมัติ",
    ],
    letterStartCta: "เริ่มลงทะเบียนบริษัท",

    formBack: "← กลับไปดูจดหมาย",
    formHeading: "ข้อมูลบริษัทของคุณ",
    formInviteFrom: (agentName) => `คำเชิญจาก ${agentName}`,
    fieldLegalName: "ชื่อบริษัทตามกฎหมาย",
    fieldLegalNamePlaceholder: "เช่น บริษัท รุ่งเรืองก่อสร้าง จำกัด",
    fieldDisplayName: "ชื่อที่ใช้เรียกทั่วไป",
    fieldDisplayNamePlaceholder: "เช่น รุ่งเรืองก่อสร้าง",
    fieldTaxId: "เลขประจำตัวผู้เสียภาษี",
    fieldContactName: "ชื่อผู้ติดต่อ",
    fieldPhone: "เบอร์โทรติดต่อ",
    fieldPhonePlaceholder: "08X-XXX-XXXX",
    fieldAddress: "ที่อยู่บริษัท",
    workerNeedsHeading: "ความต้องการแรงงาน",
    fieldDesiredWorkerType: "ประเภทแรงงานที่ต้องการ",
    fieldDesiredWorkerTypePlaceholder: "เช่น แรงงานก่อสร้าง, แม่บ้าน, พนักงานโรงงาน",
    fieldDesiredWorkerCount: "จำนวนที่ต้องการ",
    fieldDesiredWorkerCountPlaceholder: "เช่น 5",
    fieldDesiredNationalities: "แรงงานของประเทศ",
    nationalitySriLanka: "ศรีลังกา",
    nationalityBangladesh: "บังกลาเทศ",
    nationalityLaos: "ลาว",
    nationalityVietnam: "เวียดนาม",
    nationalityMyanmar: "พม่า",
    submitButton: "ส่งข้อมูลบริษัท",
    submitButtonSubmitting: "กำลังส่ง...",
    formFineprint: "การส่งแบบฟอร์มนี้เป็นการลงทะเบียนข้อมูลบริษัทเท่านั้น ยังไม่ใช่การทำสัญญาจ้างงาน",

    errorLegalNameRequired: "กรุณากรอกชื่อบริษัทตามกฎหมาย",
    errorPhoneRequired: "กรุณากรอกเบอร์โทรติดต่อ",
    errorWorkerCountInvalid: "จำนวนที่ต้องการต้องเป็นจำนวนเต็มบวก",
    submitGenericError: "ไม่สามารถส่งข้อมูลได้ในขณะนี้ กรุณาลองใหม่อีกครั้ง",

    missingInviteTitle: "ไม่พบลิงก์คำเชิญ",
    missingInviteBody: "ลิงก์ที่เปิดไม่มีรหัสคำเชิญ กรุณาตรวจสอบลิงก์อีกครั้ง หรือติดต่อผู้ที่ส่งลิงก์นี้ให้คุณ",
    errorTitleNotFound: "ไม่พบลิงก์คำเชิญนี้",
    errorTitleGeneric: "เปิดลิงก์คำเชิญไม่สำเร็จ",
    errorBodyGeneric: "กรุณาตรวจสอบลิงก์อีกครั้ง หรือติดต่อผู้ที่ส่งลิงก์นี้ให้คุณ",
    unavailableTitle: "ลิงก์นี้ใช้งานไม่ได้แล้ว",
    unavailableDefault: "กรุณาติดต่อผู้ที่ส่งลิงก์นี้ให้คุณเพื่อขอลิงก์ใหม่",

    successTitle: "ส่งข้อมูลเรียบร้อยแล้ว",
    successDuplicateBody: "เราได้รับข้อมูลของบริษัทคุณไว้ก่อนหน้านี้แล้ว ไม่ต้องส่งซ้ำอีกครั้ง",
    successBody: "ข้อมูลของคุณถูกส่งให้ผู้ดูแลแล้ว",
    successFollowup: (name) =>
      `ขั้นตอนถัดไป ${name} จะติดต่อกลับเพื่อยืนยันข้อมูล การส่งแบบฟอร์มนี้ยังไม่ใช่การทำสัญญาจ้างงาน`,
    successFallbackName: "ผู้ดูแลของคุณ",
  },

  en: {
    topbarTag: "Employer Company Registration",

    loadingText: "Opening your invitation letter...",

    envelopeEyebrow: "Employer Registration Invitation Letter",
    envelopeFrom: (agentName) => `You've received an invitation from ${agentName}`,
    envelopeHint: "Tap to open the letter and read the details",
    envelopeOpenCta: "Open Letter",
    envelopeFallbackAgent: "your Agent",

    letterEyebrow: "Employer Registration Invitation Letter",
    letterTitle: (agentName, appName) => `${agentName} invites your company to join ${appName}`,
    letterDesc:
      "Fill in your company details to get started. Once you submit, your Agent sees your information immediately.",
    explainLines: [
      "This step registers your company's details",
      "Your Agent will then contact you to confirm the information",
      "This is not an employment contract yet, and there is no automatic worker matching",
    ],
    letterStartCta: "Start company registration",

    formBack: "← Back to letter",
    formHeading: "Your company details",
    formInviteFrom: (agentName) => `Invitation from ${agentName}`,
    fieldLegalName: "Legal company name",
    fieldLegalNamePlaceholder: "e.g. Rungruang Construction Co., Ltd.",
    fieldDisplayName: "Common / trading name",
    fieldDisplayNamePlaceholder: "e.g. Rungruang Construction",
    fieldTaxId: "Tax identification number",
    fieldContactName: "Contact person's name",
    fieldPhone: "Contact phone number",
    fieldPhonePlaceholder: "08X-XXX-XXXX",
    fieldAddress: "Company address",
    workerNeedsHeading: "Worker needs",
    fieldDesiredWorkerType: "Type of worker needed",
    fieldDesiredWorkerTypePlaceholder: "e.g. construction labor, housekeeping, factory staff",
    fieldDesiredWorkerCount: "Number of workers needed",
    fieldDesiredWorkerCountPlaceholder: "e.g. 5",
    fieldDesiredNationalities: "Worker nationality",
    nationalitySriLanka: "Sri Lanka",
    nationalityBangladesh: "Bangladesh",
    nationalityLaos: "Laos",
    nationalityVietnam: "Vietnam",
    nationalityMyanmar: "Myanmar",
    submitButton: "Submit company details",
    submitButtonSubmitting: "Sending...",
    formFineprint: "Submitting this form only registers your company details. It is not an employment contract yet.",

    errorLegalNameRequired: "Please enter your legal company name",
    errorPhoneRequired: "Please enter a contact phone number",
    errorWorkerCountInvalid: "The number of workers needed must be a positive whole number",
    submitGenericError: "Unable to submit right now. Please try again.",

    missingInviteTitle: "No invitation link found",
    missingInviteBody:
      "The link you opened has no invite code. Please check the link again, or contact whoever sent it to you.",
    errorTitleNotFound: "This invitation link was not found",
    errorTitleGeneric: "Could not open the invitation link",
    errorBodyGeneric: "Please check the link again, or contact whoever sent it to you.",
    unavailableTitle: "This link is no longer usable",
    unavailableDefault: "Please contact whoever sent you this link for a new one.",

    successTitle: "Your details were submitted",
    successDuplicateBody: "We already had your company's details on file. No need to submit again.",
    successBody: "Your details have been sent to your Agent.",
    successFollowup: (name) =>
      `Next, ${name} will contact you to confirm the information. Submitting this form is not an employment contract yet.`,
    successFallbackName: "your Agent",
  },
};

/** Returns the translation dictionary for `language`, falling back to Thai for an unknown/unset code. */
export function getPublicEmployerText(language) {
  return TRANSLATIONS[language] ?? TRANSLATIONS.th;
}

/**
 * Best-effort mapping from the card's own free-text intendedLanguageCode (set
 * by the Agent, never normalized on the backend) to one of the two supported
 * UI language codes - used only to pick the default selected language, never to
 * alter what's displayed about the invite itself. Returns null (page defaults
 * to Thai) for an unknown/blank value.
 */
export function inferDefaultEmployerLanguage(card) {
  const lang = (card?.intendedLanguageCode || "").trim().toLowerCase();
  if (lang.startsWith("eng") || lang === "en") {
    return "en";
  }
  if (lang.startsWith("thai") || lang === "th" || lang.startsWith("ไทย")) {
    return "th";
  }
  return null;
}
